import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Store, Check, Star } from "lucide-react";
import Reveal from "@/components/landing/Reveal";
import AnimatedProgress from "@/components/landing/AnimatedProgress";
import { renderTitle, toFullUrl, MXN, StageBadge, SUC_ESTADO } from "@/components/landing/utils";

function ProjectCard({ p, i }) {
  const sucs = p.sucursales || [];
  const [sel, setSel] = useState(0);
  const s = sucs[sel];
  const target = Number(p.capital_target) || 0;
  const committed = Number(p.capital_committed) || 0;
  const pct = target ? Math.min(100, (committed / target) * 100) : 0;
  const img = toFullUrl(s?.image_url) || toFullUrl(p.image_url);
  const highlights = (s?.highlights || []).slice(0, 4);
  const estado = SUC_ESTADO[s?.estado] || { label: (s?.estado || "").replace(/_/g, " "), cls: "text-[#52525B] bg-black/[0.04]" };

  return (
    <div className="glass overflow-hidden h-full flex flex-col group" data-testid={`project-card-${i}`}>
      <Link to={`/proyecto/${encodeURIComponent(p.code)}`} className="block aspect-[16/10] overflow-hidden bg-[#18181B] relative" data-testid={`project-detail-link-${i}`}>
        {img && <img key={img} loading="lazy" decoding="async" src={img} alt={s?.nombre || p.code} className="w-full h-full object-cover fade-up group-hover:scale-105 transition-transform duration-700" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/20 to-transparent" />
        <div className="absolute top-3 right-3"><StageBadge stage={p.stage} /></div>
        {s && (
          <div className="absolute bottom-3 left-4 right-4">
            <div className="text-[9px] uppercase tracking-[0.2em] font-mono text-[#FFBF57]">{s.tipo || "Sucursal del proyecto"}</div>
            <div className="text-white font-display text-lg leading-tight truncate">{s.nombre}</div>
          </div>
        )}
      </Link>
      <div className="p-6 flex flex-col gap-4 flex-1">
        <div>
          <Link to={`/proyecto/${encodeURIComponent(p.code)}`} className="font-display text-2xl text-[#0A0A0A] hover:text-[#B8862F] transition-colors">{p.code}</Link>
          <div className="text-[11px] text-[#52525B] mt-0.5 flex items-center gap-1.5"><MapPin size={11} className="text-[#FFBF57]" /> {p.city}</div>
          {p.type && <div className="text-[10px] uppercase tracking-widest text-[#FFBF57] font-mono mt-2">{p.type}</div>}
        </div>

        {s && (
          <div className="rounded-2xl bg-[#FAFAFA] border border-black/6 p-4" data-testid={`project-${i}-selected-info`}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-[#52525B] flex items-center gap-1.5 min-w-0"><MapPin size={11} className="shrink-0" /><span className="truncate">{s.ciudad}</span></div>
              <span className={`text-[9px] uppercase tracking-widest font-mono px-2 py-0.5 rounded shrink-0 ${estado.cls}`}>{estado.label}</span>
            </div>
            {s.descripcion && <p className="text-xs text-[#3F3F46] leading-relaxed mt-2">{s.descripcion}</p>}
            {highlights.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {highlights.map((h, k) => (
                  <li key={k} className="flex items-start gap-2 text-[11px] text-[#0A0A0A]"><Check size={11} className="text-[#FFBF57] mt-0.5 shrink-0" strokeWidth={2.5} />{h}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div>
          <div className="flex justify-between items-baseline mb-1.5">
            <span className="text-[10px] uppercase tracking-widest text-[#52525B] font-mono">Capital objetivo</span>
            <span className="text-sm num-mono text-[#0A0A0A]">{MXN.format(target)}</span>
          </div>
          <AnimatedProgress value={pct} />
          <div className="flex justify-between text-[11px] mt-2">
            <span className="text-[#FFBF57] num-mono">{MXN.format(committed)}</span>
            <span className="text-[#52525B] num-mono">{pct.toFixed(0)}% formalizado</span>
          </div>
        </div>

        {sucs.length > 0 && (
          <div className="pt-4 border-t border-black/6 mt-auto">
            <div className="text-[10px] uppercase tracking-widest text-[#52525B] font-mono mb-3 flex items-center justify-between">
              <span>Sucursales del proyecto · toca para ver</span>
              <span className="text-[#FFBF57]">{sucs.length}{p.sucursales_target ? `/${p.sucursales_target}` : ""}</span>
            </div>
            <div className="space-y-1.5">
              {sucs.map((x, k) => {
                const e = SUC_ESTADO[x.estado] || { label: (x.estado || "").replace(/_/g, " "), cls: "text-[#52525B] bg-black/[0.04]" };
                const active = k === sel;
                return (
                  <button key={k} type="button" onClick={() => setSel(k)} data-testid={`project-${i}-sucursal-${k}`}
                    className={`w-full flex items-center gap-3 rounded-xl px-2 py-1.5 text-left transition-colors ${active ? "bg-[#0A0A0A] text-white" : "hover:bg-black/[0.04]"}`}>
                    {toFullUrl(x.image_url) ? (
                      <img loading="lazy" decoding="async" src={toFullUrl(x.image_url)} alt={x.nombre} className="w-9 h-9 rounded-lg object-cover bg-[#E4E4E7]" />
                    ) : (
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${active ? "bg-[#FFBF57]" : "bg-[#FFBF57]/12 border border-[#FFBF57]/25"}`}><Store size={12} className="text-[#0A0A0A]" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs truncate ${active ? "text-white" : "text-[#0A0A0A]"}`}>{x.nombre}</div>
                      <div className={`text-[10px] truncate ${active ? "text-white/60" : "text-[#52525B]"}`}>{x.ciudad}</div>
                    </div>
                    <span className={`text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded shrink-0 ${active ? "text-[#FFBF57] bg-white/10" : e.cls}`}>{e.label}</span>
                    {active && <ArrowRight size={12} className="text-[#FFBF57] shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProjectsSection({ block }) {
  const items = block?.items || [];
  return (
    <section id="proyectos" className="section-elev py-20 md:py-32 relative overflow-hidden">
      <div className="gold-glow" style={{ width: 480, height: 480, top: 120, right: -140, opacity: 0.5 }} />
      <div className="max-w-[1300px] mx-auto px-5 sm:px-6 lg:px-10 relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <Reveal><span className="eyebrow">Convocatoria activa</span></Reveal>
            <Reveal delay={120}>
              <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl text-[#0A0A0A] max-w-3xl">{renderTitle(block?.title || "Dos proyectos. Participaciones <em>limitadas</em>.")}</h2>
            </Reveal>
          </div>
          {block?.note && <Reveal delay={200}><p className="text-sm text-[#3F3F46] max-w-sm leading-relaxed">{block.note}</p></Reveal>}
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {items.map((p, i) => <Reveal key={p.code || i} delay={i * 140}><ProjectCard p={p} i={i} /></Reveal>)}
          <Reveal delay={items.length * 140}>
            <div className="h-full rounded-3xl bg-[#0A0A0A] on-onyx text-white p-7 md:p-8 flex flex-col relative overflow-hidden border border-[#FFBF57]/30" data-testid="upcoming-projects-card">
              <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-[#FFBF57]/15 blur-3xl" />
              <div className="absolute inset-0 grid-lines opacity-20 pointer-events-none" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-mono text-[#FFBF57] border border-[#FFBF57]/40 px-3 py-1.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FFBF57] animate-pulse" /> Próximamente
                </div>
                <div className="mt-6 font-display text-2xl md:text-3xl leading-tight">{block?.upcoming_title || "Próximamente nuevos proyectos"}</div>
                <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">{block?.upcoming_body || "Los siguientes proyectos de participación se anunciarán primero a nuestra comunidad de inversionistas."}</p>
                <div className="mt-6 pt-5 border-t border-white/10">
                  <div className="text-[10px] uppercase tracking-widest text-[#FFBF57] font-mono flex items-center gap-2"><Star size={11} /> ¿Ya eres inversionista?</div>
                  <ul className="mt-3 space-y-2">
                    {(block?.upcoming_benefits?.length ? block.upcoming_benefits : ["Acceso preferente a la información de cada nuevo proyecto", "Prioridad para incorporarte antes de la convocatoria pública", "Beneficios especiales por ser inversionista Rubí"]).map((b, k) => (
                      <li key={k} className="flex items-start gap-2 text-sm text-white/90"><Check size={13} className="text-[#FFBF57] mt-0.5 shrink-0" strokeWidth={2.5} />{b}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <Link to="/calificacion" className="relative btn-primary !bg-[#FFBF57] !text-[#0A0A0A] justify-center mt-auto pt-3" data-testid="upcoming-cta">Quiero enterarme primero <ArrowRight size={14} /></Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
